/**
 * Helpers puros de presentación del historial de actividad: meta de tipos,
 * chips de filtro, agrupado por día, etiquetas de fecha/duración y cálculo de
 * racha. Sin JSX — los iconos viven en `ActivityRow`.
 */
import { formatChatMinutesShort } from '../../lib/chatMinutes.js';
import type { SessionActivityType, SessionHistoryItem } from '../../lib/sessionsApi.js';
import type { DailyProgress } from '../../lib/englishApi.js';
import type { ActivityFilter } from '../../hooks/useActivity.js';

export interface ActivityTypeMeta {
  label: string;
  color: string;
}

/** Meta por tipo (colores del diseño V3). */
export const ACTIVITY_TYPES: Record<SessionActivityType, ActivityTypeMeta> = {
  conversation: { label: 'Conversación', color: '#a78bfa' },
  lesson: { label: 'Lección', color: '#5ce1e6' },
  drill: { label: 'Drill', color: '#fbbf24' },
  review: { label: 'Repaso', color: '#d4f23e' },
};

/**
 * What kind of conversation a history row is, so each reads apart at a glance:
 * plain free talk, a role-play (`scenarioId`), a Program session (`mode:
 * 'plan'`), the calibration exam, and free talk about something the learner
 * brought from the Audioteca, a news article or Órbitas (`origin`). A
 * role-play wins over an origin (they never coexist: content hints only apply
 * outside a scenario).
 */
export type ConversationKind =
  | 'free'
  | 'scenario'
  | 'program'
  | 'calibration'
  | 'listen'
  | 'news'
  | 'orbit';

export interface ConversationKindMeta {
  /** Badge text on the row and the detail's kicker. */
  label: string;
  /** Identity color (a token of tokens.css). */
  color: string;
}

/**
 * Each kind's identity, the one it has elsewhere in speak: the Program is
 * coral, role-plays purple, the Audioteca cyan ("Escuchar"), Órbitas lime.
 * Plain free talk is the one WITHOUT a borrowed identity: the neutral text
 * tone, so the colored rows read as "about something" next to it.
 */
export const CONVERSATION_KINDS: Record<ConversationKind, ConversationKindMeta> = {
  free: { label: 'Charla libre', color: 'var(--text)' },
  scenario: { label: 'Escenario', color: 'var(--purple)' },
  program: { label: 'Programa', color: 'var(--coral)' },
  calibration: { label: 'Examen', color: 'var(--streak)' },
  listen: { label: 'Audioteca', color: 'var(--cyan)' },
  news: { label: 'Noticia', color: 'var(--reviewed)' },
  orbit: { label: 'Órbitas', color: 'var(--lime)' },
};

export function conversationKind(
  item: Pick<SessionHistoryItem, 'mode' | 'scenarioId' | 'origin'>,
): ConversationKind {
  if (item.mode === 'calibration') return 'calibration';
  if (item.scenarioId != null) return 'scenario';
  if (item.mode === 'plan') return 'program';
  if (item.origin) return item.origin.kind;
  return 'free';
}

export interface ActivityFilterDef {
  id: ActivityFilter;
  label: string;
}

/** Chips del filtro (los 4 tipos + "Todo"). Hoy sólo conversaciones traen datos. */
export const ACTIVITY_FILTERS: ReadonlyArray<ActivityFilterDef> = [
  { id: 'all', label: 'Todo' },
  { id: 'conversation', label: 'Conversaciones' },
  { id: 'lesson', label: 'Lecciones' },
  { id: 'drill', label: 'Drills' },
  { id: 'review', label: 'Repasos' },
];

export type DayBucket = 'Hoy' | 'Ayer' | 'Esta semana' | 'Más antiguo';
export const ACTIVITY_DAY_ORDER: ReadonlyArray<DayBucket> = [
  'Hoy',
  'Ayer',
  'Esta semana',
  'Más antiguo',
];

const MONTHS_ES = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];
const WEEKDAYS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

/** Diferencia en días calendario (local) entre `now` y `d` (>=0 = d en el pasado). */
function dayDiff(d: Date, now: Date): number {
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((b - a) / 86_400_000);
}

export function dayBucket(d: Date, now: Date): DayBucket {
  const diff = dayDiff(d, now);
  if (diff <= 0) return 'Hoy';
  if (diff === 1) return 'Ayer';
  if (diff < 7) return 'Esta semana';
  return 'Más antiguo';
}

/** Etiqueta corta para el extremo de la fila (hora hoy/ayer, día si más viejo). */
export function whenLabel(d: Date, now: Date): string {
  const diff = dayDiff(d, now);
  if (diff <= 1) {
    return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  if (diff < 7) {
    return WEEKDAYS_ES[d.getDay()] ?? '';
  }
  return `${d.getDate()} ${MONTHS_ES[d.getMonth()] ?? ''}`;
}

/** Etiqueta de fecha completa para el detalle ("Hoy · 9:41", "31 may · 19:05"). */
export function whenFull(d: Date, now: Date): string {
  const time = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  const diff = dayDiff(d, now);
  if (diff <= 0) return `Hoy · ${time}`;
  if (diff === 1) return `Ayer · ${time}`;
  if (diff < 7) return `${WEEKDAYS_ES[d.getDay()] ?? ''} · ${time}`;
  return `${d.getDate()} ${MONTHS_ES[d.getMonth()] ?? ''} · ${time}`;
}

/**
 * Duración compacta para las micro-stats de la fila ("6m" / "<1m" / null).
 * Se alimenta de `activeSeconds` (tiempo activo), NO del wall-clock: ver
 * {@link weekMinutesFromDaily}.
 *
 * Delega el redondeo en {@link formatChatMinutesShort}, la regla única de la
 * app. Antes cortaba en 60 s por su cuenta y el resto de las pantallas
 * redondeaba: la misma charla de 45 s decía "<1m" acá y "1 min" en la Home.
 */
export function durationShort(seconds: number | null): string | null {
  if (seconds == null) return null;
  return formatChatMinutesShort(seconds);
}

export interface DayGroup {
  day: DayBucket;
  items: SessionHistoryItem[];
}

/** Agrupa por bucket de día preservando ACTIVITY_DAY_ORDER; omite vacíos. */
export function groupByDay(items: ReadonlyArray<SessionHistoryItem>, now: Date): DayGroup[] {
  const byBucket = new Map<DayBucket, SessionHistoryItem[]>();
  for (const item of items) {
    const bucket = dayBucket(new Date(item.createdAt), now);
    const arr = byBucket.get(bucket);
    if (arr) arr.push(item);
    else byBucket.set(bucket, [item]);
  }
  return ACTIVITY_DAY_ORDER.map((day) => ({ day, items: byBucket.get(day) ?? [] })).filter(
    (g) => g.items.length > 0,
  );
}

/** Etiqueta `YYYY-MM-DD` del día LOCAL de `d` — la misma que emite `/progress/daily`. */
function localDayLabel(d: Date): string {
  return new Intl.DateTimeFormat('en-CA').format(d);
}

/**
 * Minutos de charla de los últimos `days` días (hoy incluido) leídos de
 * `/progress/daily`.
 *
 * Dos razones para no sumarlos client-side sobre el historial cargado (que es
 * lo que hacía `totalMinutesWithinDays`, ya retirado):
 *   1. **Definición**: los minutos de la app son tiempo ACTIVO
 *      (`voice_sessions.active_seconds`), no wall-clock — una sesión dejada
 *      abierta aportaba horas al total de la semana. Es la misma señal que la
 *      Home, el heatmap y el desglose de la hoja de racha.
 *   2. **Paginación**: el historial se pagina de a 20 ítems, así que una semana
 *      intensa quedaba subestimada y el número CAMBIABA al scrollear (el mismo
 *      motivo por el que la racha ya se había mudado al backend).
 *
 * `null` = sin dato (cargando o error de red): la superficie muestra "—" en vez
 * de un 0 que es indistinguible de "no practicaste".
 *
 * Se suman los minutos YA redondeados de cada día (es lo que `/progress/daily`
 * expone), así que el total es exactamente la suma de los días que pinta el
 * heatmap — no la suma de los segundos crudos: siete días de 29 s dan 0.
 */
export function weekMinutesFromDaily(
  daily: DailyProgress | null,
  days: number,
  now: Date,
): number | null {
  if (!daily) return null;
  const first = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  const fromLabel = localDayLabel(first);
  let total = 0;
  for (const d of daily.days) {
    // Etiquetas `YYYY-MM-DD`: la comparación lexicográfica ES la cronológica.
    if (d.date >= fromLabel) total += d.minutes;
  }
  return total;
}

// computeStreakDays se eliminó: la racha viene del backend (/progress/totals
// vía useProgressTotals) — el cómputo client-side sobre los items cargados
// subestimaba con historial paginado y divergía entre superficies.
